// Session 27: the lesson block components.
//
// Rebuilt in the portal's own idiom. The tennis course components were the
// design reference, not the source: that app is Next.js + Tailwind + MDX and
// this one is Vite + plain JSX + styles.css, so porting would have meant
// bringing a second styling system in. See the reuse report.
//
// Every block takes `readOnly`. In Preview the sample lesson is readable but
// nothing is recorded, because portal_course_* RPCs refuse to write unless the
// client has the course switched on. So an interactive block in Preview shows
// its controls disabled rather than failing a save.

import { useState } from 'react';
import { SaveStatus, useSaveStatus } from '../SaveStatus.jsx';
import { supabase } from '../../supabase.js';

/* ---------------------------------------------------------------- inline bits */

// The approved lesson copy uses a bold lead-in on some list items ("**Role.**
// Who it should be..."). This renders exactly that and nothing else: no
// markdown parser, no HTML from content, so a lesson file cannot inject markup.
function Rich({ text }) {
  const parts = String(text).split(/\*\*(.+?)\*\*/g);
  return (
    <>
      {parts.map((part, i) => (i % 2 === 1 ? <strong key={i}>{part}</strong> : part))}
    </>
  );
}

export function Prose({ body }) {
  return (
    <>
      {(body || []).map((b, i) => {
        if (b.type === 'p') return <p key={i}><Rich text={b.text} /></p>;
        if (b.type === 'list') {
          return (
            <ul key={i} className="course-list">
              {b.items.map((it, j) => <li key={j}><Rich text={it} /></li>)}
            </ul>
          );
        }
        if (b.type === 'deflist') {
          return (
            <dl key={i} className="course-deflist">
              {b.items.map((it, j) => (
                <div key={j} className="course-def">
                  <dt>{it.term}</dt>
                  <dd>{it.text}</dd>
                </div>
              ))}
            </dl>
          );
        }
        if (b.type === 'steps') {
          return (
            <ol key={i} className="course-steps">
              {b.items.map((it, j) => <li key={j}><Rich text={it} /></li>)}
            </ol>
          );
        }
        // A subheading inside one block, so a long section keeps the template
        // label it belongs to instead of being split into several blocks.
        if (b.type === 'sub') return <h3 key={i} className="course-sub">{b.text}</h3>;
        if (b.type === 'rule') return <p key={i} className="course-rule">{b.text}</p>;
        if (b.type === 'callout') return <Callout key={i} tone={b.tone} text={b.text} />;
        if (b.type === 'expand') return <Expand key={i} summary={b.summary} body={b.body} />;
        return null;
      })}
    </>
  );
}

export function Callout({ tone = 'info', text }) {
  return <p className={`course-callout course-callout-${tone}`}>{text}</p>;
}

// The tennis course's Expandable: long detail collapsed so the core read stays
// short. <details> needs no state and keeps keyboard behaviour for free.
export function Expand({ summary, body }) {
  return (
    <details className="course-expand">
      <summary>{summary}</summary>
      <div className="course-expand-body"><Prose body={body} /></div>
    </details>
  );
}

/* -------------------------------------------------------------------- blocks */

export function ConceptBlock({ block }) {
  return (
    <section className="course-block">
      {block.heading && <h2>{block.heading}</h2>}
      <Prose body={block.body} />
    </section>
  );
}

// Tool tabs appear ONLY where the clicks differ. The learner's pick is kept in
// component state, not saved: it is a reading preference, not an answer.
export function TabsBlock({ block, isAgency }) {
  const [active, setActive] = useState(0);
  const tab = block.tabs[active];
  return (
    <section className="course-block">
      {block.heading && <h2>{block.heading}</h2>}
      {block.verify && isAgency && (
        <p className="course-callout course-callout-verify">
          Agency note: these settings paths are not verified against the live products yet.
          Check each one before this lesson is published.
        </p>
      )}
      {block.intro && <p>{block.intro}</p>}
      <div className="course-tabs" role="tablist">
        {block.tabs.map((t, i) => (
          <button
            key={t.tool}
            type="button"
            role="tab"
            aria-selected={i === active}
            className={`course-tab ${i === active ? 'course-tab-on' : ''}`}
            onClick={() => setActive(i)}
          >
            {t.tool}
          </button>
        ))}
      </div>
      <ol className="course-steps">
        {tab.steps.map((s, i) => <li key={i}>{s}</li>)}
      </ol>
      {block.callout && <Callout tone={block.callout.tone} text={block.callout.text} />}
    </section>
  );
}

// "Your turn", on the learner's own real work. Each step has its own tick, so
// the lesson can only be completed once the work is actually done.
export function ExerciseBlock({ block, courseKey, lessonKey, done, onToggle, readOnly }) {
  return (
    <section className="course-block">
      {block.heading && <h2>{block.heading}</h2>}
      {block.intro && <p>{block.intro}</p>}
      <ul className="checklist course-exercise">
        {block.steps.map((s) => {
          const isDone = !!done[s.id];
          return (
            <li key={s.id} className={`check-item ${isDone ? 'check-done' : 'check-todo'}`}>
              <label className="check-label">
                <input
                  type="checkbox"
                  id={`ex-${courseKey}-${lessonKey}-${s.id}`}
                  checked={isDone}
                  disabled={readOnly}
                  onChange={(e) => onToggle(s.id, e.target.checked)}
                />
                <span>{s.text}</span>
              </label>
            </li>
          );
        })}
      </ul>
      {readOnly && <p className="course-muted">This is a free sample, so nothing you tick here is saved.</p>}
    </section>
  );
}

// 2 or 3 multiple choice. Answering shows whether it was right and why; the
// answer is saved so the agency can see where a lesson lands badly.
export function QuickCheckBlock({ block, courseKey, lessonKey, readOnly }) {
  const [picked, setPicked] = useState({});
  const save = useSaveStatus();

  async function choose(qi, oi) {
    setPicked((p) => ({ ...p, [qi]: oi }));
    if (readOnly) return;
    save.start();
    const { error } = await supabase.rpc('portal_course_save_response', {
      p_course_key: courseKey,
      p_lesson_key: lessonKey,
      p_kind: 'quick_check',
      // Session 27b: the slot is what makes this one row per question. Without
      // it every answer in a lesson overwrote the one before.
      p_slot: String(qi),
      p_payload: { question_index: qi, choice: oi, correct: oi === block.questions[qi].answer },
    });
    if (error) return save.fail(error.message);
    save.ok();
  }

  return (
    <section className="course-block">
      {/* Leena, 2026-10-09: these are genuinely optional. Nothing is gated on
          them, so the heading says so rather than leaving a reader who would
          rather skip wondering whether they are allowed to. */}
      <h2>
        Quick check <span className="course-optional">optional</span>
      </h2>
      {block.questions.map((q, qi) => {
        const pick = picked[qi];
        const answered = pick !== undefined;
        const right = answered && pick === q.answer;
        return (
          <div key={qi} className="course-q">
            <p className="course-q-text">{q.q}</p>
            <div className="course-q-options">
              {q.options.map((opt, oi) => (
                <button
                  key={oi}
                  type="button"
                  className={
                    'course-opt' +
                    (answered && oi === q.answer ? ' course-opt-right' : '') +
                    (answered && oi === pick && oi !== q.answer ? ' course-opt-wrong' : '')
                  }
                  disabled={answered}
                  onClick={() => choose(qi, oi)}
                >
                  {opt}
                </button>
              ))}
            </div>
            {answered && (
              <p className={`course-q-why ${right ? 'course-q-right' : 'course-q-wrong'}`}>
                {right ? 'That is right. ' : 'Not quite. '}{q.because}
              </p>
            )}
          </div>
        );
      })}
      <p className="course-muted">
        Skipping these holds nothing back. You can still finish the lesson.
      </p>
      <SaveStatus status={save.status} />
    </section>
  );
}

// The prompt card. Saving it puts a copy in the learner's own library, which is
// the thing they keep after the course ends.
export function PromptCardBlock({ block, courseKey, lessonKey, readOnly, onSaved }) {
  const [copied, setCopied] = useState(false);
  const [saved, setSaved] = useState(false);
  const save = useSaveStatus();

  async function copy() {
    try {
      await navigator.clipboard.writeText(block.body);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // Clipboard can be blocked; the prompt is selectable on the page anyway.
    }
  }

  async function keep() {
    // Session 27b: `saved` only flips after the RPC resolves, so without the
    // in-flight check a double-click fired two inserts. The RPC is now an
    // upsert as well, so this is belt and braces.
    if (readOnly || saved || save.status === 'saving') return;
    save.start();
    const { error } = await supabase.rpc('portal_course_save_prompt', {
      p_course_key: courseKey,
      p_lesson_key: lessonKey,
      p_title: block.title,
      p_body: block.body,
      p_source: 'lesson',
    });
    if (error) return save.fail(error.message);
    setSaved(true);
    save.ok();
    if (onSaved) onSaved();
  }

  return (
    <section className="course-block">
      <h2>Prompt card</h2>
      <div className="course-prompt">
        <p className="course-prompt-title">{block.title}</p>
        <blockquote className="course-prompt-body">{block.body}</blockquote>
        {/* Leena, 2026-10-09: a complete beginner does not know that a prompt
            and the material go in the same message. The first card in a course
            spells it out; later ones leave howTo off. */}
        {block.howTo && <p className="course-prompt-how">{block.howTo}</p>}
        <div className="course-prompt-actions">
          <button type="button" className="btn" onClick={copy}>
            {copied ? 'Copied' : 'Copy'}
          </button>
          <button type="button" className="btn btn-primary" onClick={keep} disabled={readOnly || saved}>
            {saved ? 'In your library' : 'Save to my library'}
          </button>
          <SaveStatus status={save.status} />
        </div>
      </div>
      {block.note && <p className="course-muted course-prompt-note">{block.note}</p>}
    </section>
  );
}

export function TakeawayBlock({ block }) {
  return (
    <section className="course-block">
      <p className="course-takeaway">{block.text}</p>
    </section>
  );
}

// Every lesson ends with this. The answers are the upsell list Leena reads, so
// the copy asks for a task rather than for feedback on the lesson.
export function WhatsMissingBlock({ courseKey, lessonKey, readOnly }) {
  const [text, setText] = useState('');
  const save = useSaveStatus();

  async function send() {
    if (readOnly || !text.trim()) return;
    save.start();
    const { error } = await supabase.rpc('portal_course_save_response', {
      p_course_key: courseKey,
      p_lesson_key: lessonKey,
      p_kind: 'whats_missing',
      p_payload: { text: text.trim() },
    });
    if (error) return save.fail(error.message);
    save.ok();
  }

  return (
    <section className="course-block course-missing">
      <h2>What&rsquo;s missing?</h2>
      <p>What is a task you wanted help with that this did not cover?</p>
      <textarea
        id={`missing-${courseKey}-${lessonKey}`}
        className="course-textarea"
        rows={3}
        value={text}
        disabled={readOnly}
        onChange={(e) => setText(e.target.value)}
        placeholder="Anything at all. It goes straight to Digital Lotus."
      />
      <div className="course-prompt-actions">
        <button type="button" className="btn" onClick={send} disabled={readOnly || !text.trim()}>
          Send to Digital Lotus
        </button>
        <SaveStatus status={save.status} />
      </div>
    </section>
  );
}

// Built now, hidden until there is video. One 2-minute walkthrough per lesson.
export function VideoSlot({ note, isAgency }) {
  if (!isAgency) return null;
  return (
    <section className="course-block course-videoslot">
      <p className="course-muted">
        Video slot (hidden from learners){note ? `: ${note}` : ''}
      </p>
    </section>
  );
}
